import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";

import { useResizeObserver } from "@bokio/hooks/useResizeObserver/useResizeObserver";

import type { ReactNode } from "react";

import * as style from "./animations.scss";

interface SlideAnimationProps {
	isVisible?: boolean;
	children: ReactNode;
	inline?: boolean;
}

/**
 * Animate from hidden to an estimated size (width or height).
 * The width or height will be animated to the real size of the children
 * by measuring the children once it is rendered.
 * @example <SlideAnimation isVisible={true} targetHeight={100}>Content here</SlideAnimation>
 */
const SlideAnimation = ({ isVisible, inline, children }: SlideAnimationProps) => {
	const [realHeight, setRealHeight] = useState<number | null>(null);
	const measureRef = useRef<HTMLDivElement>(null);

	const onResize = useCallback(
		// @ts-expect-error -- 2025-07-13 PontusH: noImplicitAny config rule has been enabled. Please fix this error if you are editing this file.
		rect => {
			if (!inline) {
				setRealHeight(Math.round(rect.height));
			}
		},
		[inline],
	);

	const setRealSize = useEffectEvent(() => {
		const contentRect = measureRef.current?.getBoundingClientRect();
		if (!contentRect) {
			return;
		}

		setRealHeight(Math.round(contentRect.height));
	});

	useEffect(() => {
		setRealSize();
	}, []);

	useResizeObserver({ ref: measureRef, onResize });

	return (
		<div style={{ display: inline ? "inline-block" : "block", verticalAlign: "middle" }}>
			<div
				className={style.transitions}
				style={{
					opacity: isVisible ? 1 : 0,
					height: isVisible ? (realHeight ? realHeight : "auto") : 0,
					width: isVisible ? "auto" : 0,
					visibility: isVisible ? "visible" : "hidden",
				}}
			>
				<div ref={measureRef}>{children}</div>
			</div>
		</div>
	);
};

export default SlideAnimation;
